'use client';

import { useEffect, useState, useCallback } from 'react';
import { getDayStatus, skipDay, unskipDay, type DayStatus } from '@/lib/dayTracking';
import type { UserProgramSelection } from '@/lib/types/program';

interface DayInfo {
  day: number;
  status: DayStatus;
}

interface WeekTrackerProps {
  week: number;
  currentDay: number;
  userId?: string;
  onDayClick?: (day: number) => void;
  onRefresh?: () => Promise<void>;
  programSelection?: UserProgramSelection | null;
}

export default function WeekTracker({ week, currentDay, userId, onDayClick, onRefresh, programSelection }: WeekTrackerProps) {
  const [contextMenu, setContextMenu] = useState<{ day: number; x: number; y: number } | null>(null);
  const [days, setDays] = useState<DayInfo[]>([]);
  const [completionRate, setCompletionRate] = useState(0);
  const [actionLoading, setActionLoading] = useState(false);

  // Calculate day statuses from programSelection
  const calculateDays = useCallback(() => {
    if (!programSelection) {
      // No program selected - show all days as not available
      setDays(Array.from({ length: 7 }, (_, i) => ({
        day: i + 1,
        status: 'future' as DayStatus
      })));
      setCompletionRate(0);
      return;
    }

    const dayInfos = Array.from({ length: 7 }, (_, i) => {
      const day = i + 1;
      const status = getDayStatus(programSelection, week, day);
      return { day, status };
    });

    const completedCount = dayInfos.filter(d => d.status === 'completed').length;
    const rate = (completedCount / 7) * 100;

    setDays(dayInfos);
    setCompletionRate(rate);
  }, [programSelection, week]);

  useEffect(() => {
    calculateDays();
  }, [calculateDays]);

  // Listen for visibility changes to refresh data when returning to the page
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        calculateDays();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [calculateDays]);

  const handleSkipDay = async (day: number) => {
    if (!userId) {
      console.warn('Cannot skip day: no userId provided');
      setContextMenu(null);
      return;
    }

    setActionLoading(true);
    try {
      await skipDay(userId, week, day);
      if (onRefresh) {
        await onRefresh();
      }
      calculateDays();
    } catch (error) {
      console.error('Error skipping day:', error);
    } finally {
      setActionLoading(false);
      setContextMenu(null);
    }
  };

  const handleUnskipDay = async (day: number) => {
    if (!userId) {
      console.warn('Cannot unskip day: no userId provided');
      setContextMenu(null);
      return;
    }

    setActionLoading(true);
    try {
      await unskipDay(userId, week, day);
      if (onRefresh) {
        await onRefresh();
      }
      calculateDays();
    } catch (error) {
      console.error('Error unskipping day:', error);
    } finally {
      setActionLoading(false);
      setContextMenu(null);
    }
  };

  const handleRightClick = (e: React.MouseEvent, day: number) => {
    e.preventDefault();
    setContextMenu({ day, x: e.clientX, y: e.clientY });
  };

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClick = () => setContextMenu(null);
    if (contextMenu) {
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [contextMenu]);

  const getStatusColor = (status: DayStatus, isCurrentDay: boolean) => {
    if (isCurrentDay) {
      return 'border-operator-gold bg-operator-gold/20';
    }

    switch (status) {
      case 'completed':
        return 'border-green-500 bg-green-500/20';
      case 'skipped':
        return 'border-gray-500 bg-gray-500/10';
      case 'available':
        return 'border-blue-400/50 bg-blue-500/10';
      case 'future':
        return 'border-operator-steel/30 bg-operator-gunmetal/50';
      case 'current':
        return 'border-operator-gold bg-operator-gold/20';
      default:
        return 'border-operator-steel bg-operator-gunmetal';
    }
  };

  const getStatusIcon = (status: DayStatus) => {
    switch (status) {
      case 'completed':
        // Was an empty string, so the one state worth celebrating was the only
        // one with no glyph — a finished day looked blank next to a skipped one.
        return '✓';
      case 'skipped':
        return '⊘';
      case 'current':
        return '▶';
      case 'available':
        return '○';
      case 'future':
        return '·';
      default:
        return '';
    }
  };

  const getStatusText = (status: DayStatus) => {
    switch (status) {
      case 'completed':
        return 'Done';
      case 'skipped':
        return 'Skipped';
      case 'current':
        return 'Current';
      case 'available':
        return 'Available';
      case 'future':
        return 'Locked';
      default:
        return '';
    }
  };

  const weekComplete = completionRate === 100;

  return (
    <div className="space-y-3 relative">
      {/* Week Complete Celebration */}
      {weekComplete && (
        <div className="absolute -top-2 -right-2 z-10">
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-gradient-to-r from-operator-gold to-amber-500 text-black shadow-lg shadow-operator-gold/30 animate-pulse">
            Week Complete
          </span>
        </div>
      )}

      {/* Week Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm uppercase text-gray-400 font-heading">Week {week} Progress</h3>
        <div className={`text-xs ${weekComplete ? 'text-operator-gold font-semibold' : 'text-gray-500'}`}>
          {Math.round(completionRate)}% Complete
        </div>
      </div>

      {/* Days Grid */}
      <div className="grid grid-cols-7 gap-2">
        {days.map((dayInfo) => {
          const isCurrentDay = dayInfo.status === 'current' || dayInfo.day === currentDay;
          const colorClass = getStatusColor(dayInfo.status, isCurrentDay);
          const isClickable = dayInfo.status !== 'future' && onDayClick;

          return (
            <button
              key={dayInfo.day}
              onClick={() => isClickable && onDayClick?.(dayInfo.day)}
              onContextMenu={(e) => handleRightClick(e, dayInfo.day)}
              disabled={actionLoading || dayInfo.status === 'future'}
              className={`relative aspect-square rounded-lg border-2 ${colorClass}
                ${isClickable ? 'hover:scale-105 active:scale-95 cursor-pointer' : 'cursor-default opacity-60'}
                transition-all flex flex-col items-center justify-center
                ${actionLoading ? 'opacity-50' : ''}`}
              // "Right-click" is not an instruction a phone user can follow —
              // and this is a phone-first app. The handler is onContextMenu,
              // which long-press also fires on touch, so say both.
              title={`Day ${dayInfo.day} - ${getStatusText(dayInfo.status)}${dayInfo.status !== 'future' ? ' (long-press or right-click for options)' : ''}`}
            >
              {/* Day Number */}
              <div className={`text-xs font-bold ${
                isCurrentDay ? 'text-operator-gold' :
                dayInfo.status === 'completed' ? 'text-green-400' :
                dayInfo.status === 'skipped' ? 'text-gray-500 line-through' :
                dayInfo.status === 'available' ? 'text-blue-400' :
                'text-gray-600'
              }`}>
                {dayInfo.day}
              </div>

              {/* Status Icon */}
              <div className={`text-lg mt-0.5 ${
                isCurrentDay ? 'text-operator-gold' :
                dayInfo.status === 'completed' ? 'text-green-400' :
                dayInfo.status === 'skipped' ? 'text-gray-500' :
                dayInfo.status === 'available' ? 'text-blue-400' :
                'text-gray-700'
              }`}>
                {getStatusIcon(dayInfo.status)}
              </div>

              {/* Current Day Indicator */}
              {isCurrentDay && (
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-operator-gold rounded-full border-2 border-operator-void animate-pulse" />
              )}
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center flex-wrap gap-3 text-xs pt-2 border-t border-operator-steel/30">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-sm bg-green-500/20 border border-green-500" />
          <span className="text-gray-400">Done</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-sm bg-operator-gold/20 border-2 border-operator-gold" />
          <span className="text-gray-400">Current</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-sm bg-blue-500/10 border border-blue-400/50" />
          <span className="text-gray-400">Available</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-sm bg-gray-500/10 border border-gray-500" />
          <span className="text-gray-400">Skipped</span>
        </div>
      </div>

      {/* Context Menu */}
      {contextMenu && userId && (
        <div
          className="fixed z-50 bg-operator-gunmetal border border-operator-steel rounded-lg shadow-2xl py-2 min-w-[160px]"
          style={{
            left: `${contextMenu.x}px`,
            top: `${contextMenu.y}px`,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {days[contextMenu.day - 1]?.status === 'skipped' ? (
            <button
              onClick={() => handleUnskipDay(contextMenu.day)}
              disabled={actionLoading}
              className="w-full px-4 py-2 text-left text-sm hover:bg-operator-steel/30 transition flex items-center gap-2 disabled:opacity-50"
            >
              <span>↩</span>
              <span>Unskip Day {contextMenu.day}</span>
            </button>
          ) : days[contextMenu.day - 1]?.status !== 'future' ? (
            <button
              onClick={() => handleSkipDay(contextMenu.day)}
              disabled={actionLoading}
              className="w-full px-4 py-2 text-left text-sm hover:bg-operator-steel/30 transition flex items-center gap-2 disabled:opacity-50"
            >
              <span>⊘</span>
              <span>Skip Day {contextMenu.day}</span>
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}

